import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  MessageCircle, Send, Pin, Trash2, Reply, Check, CheckCheck, 
  X, Eye, Download, FileText, BarChart3, Paperclip, Image as ImageIcon, File
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import api from '../services/api';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

const CANAIS = [
  { id: 'geral', nome: 'Geral', icon: '📢', cor: 'bg-blue-500' },
  { id: 'cafe', nome: 'Café', icon: '☕', cor: 'bg-amber-600' },
  { id: 'loja_avelal', nome: 'Loja Avelal', icon: '🏪', cor: 'bg-green-600' },
  { id: 'loja_satao', nome: 'Loja Sátão', icon: '🏬', cor: 'bg-purple-600' }
];

const ChatInterno = ({ user, onMensagemEnviada, scrollToUnread = false, targetInfo = null, onTargetHandled = () => {} }) => {
  const [mensagens, setMensagens] = useState([]);
  const [canalAtivo, setCanalAtivo] = useState('geral');
  const [novaMensagem, setNovaMensagem] = useState('');
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [respondendoA, setRespondendoA] = useState(null);
  const [showLeituras, setShowLeituras] = useState(null);
  const [showEstatisticas, setShowEstatisticas] = useState(false);
  const [estatisticas, setEstatisticas] = useState(null);
  const [naoLidas, setNaoLidas] = useState(0);
  const [anexos, setAnexos] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Estado para mensagens diretas
  const [modoChat, setModoChat] = useState('canais'); // 'canais' ou 'direto'
  const [destinatarios, setDestinatarios] = useState([]);
  const [conversas, setConversas] = useState([]);
  const [conversaAtiva, setConversaAtiva] = useState(null);
  const [mensagensDiretas, setMensagensDiretas] = useState([]);
  const [naoLidasDiretas, setNaoLidasDiretas] = useState(0);
  
  const messagesEndRef = useRef(null);
  const firstUnreadRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Função para fazer scroll até a primeira mensagem não lida
  const scrollToFirstUnread = useCallback(() => {
    setTimeout(() => {
      if (firstUnreadRef.current) {
        firstUnreadRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        // Se não houver mensagens não lidas, ir para o fim
        scrollToBottom();
      }
    }, 300);
  }, []);

  // Efeito para navegar para canal/conversa específica quando recebe targetInfo
  useEffect(() => {
    if (targetInfo) {
      console.log('ChatInterno: targetInfo recebido:', targetInfo);
      
      // Mudar para o modo correcto (canais ou direto)
      if (targetInfo.modoChat === 'direto') {
        setModoChat('direto');
        
        // Se tem ID do utilizador, abrir essa conversa
        if (targetInfo.conversaUserId) {
          // Buscar dados completos do utilizador para a conversa
          const buscarDadosUtilizador = async () => {
            try {
              console.log('ChatInterno: Buscando dados para conversaUserId:', targetInfo.conversaUserId);
              
              // Primeiro, tentar buscar das conversas existentes
              const conversasResp = await api.get(`/chat/direto/conversas`);
              const conversas = conversasResp.data || [];
              console.log('ChatInterno: Conversas encontradas:', conversas.length, conversas.map(c => ({ id: c.user_id, nome: c.user_nome })));
              
              const conversaExistente = conversas.find(c => c.user_id === targetInfo.conversaUserId);
              
              if (conversaExistente) {
                console.log('ChatInterno: Conversa existente encontrada:', conversaExistente);
                setConversaAtiva(conversaExistente);
                // As mensagens serão carregadas pelo outro useEffect quando conversaAtiva mudar
              } else {
                console.log('ChatInterno: Conversa NÃO encontrada nas existentes, buscando destinatários...');
                // Se não encontrou nas conversas, buscar dos destinatários (todos os utilizadores)
                const destResp = await api.get(`/chat/direto/destinatarios`);
                const destinatarios = destResp.data || [];
                console.log('ChatInterno: Destinatários encontrados:', destinatarios.length);
                
                const destinatario = destinatarios.find(d => d.id === targetInfo.conversaUserId);
                
                if (destinatario) {
                  console.log('ChatInterno: Destinatário encontrado:', destinatario);
                  setConversaAtiva({
                    user_id: destinatario.id,
                    user_nome: destinatario.nome,
                    user_role: destinatario.role
                  });
                } else {
                  // Último recurso: usar só o ID
                  console.log('ChatInterno: Utilizador NÃO encontrado em nenhuma lista, usando apenas ID:', targetInfo.conversaUserId);
                  setConversaAtiva({ user_id: targetInfo.conversaUserId, user_nome: 'Utilizador', user_role: '' });
                }
              }
            } catch (error) {
              console.error('ChatInterno: Erro ao buscar dados do utilizador:', error);
              // Fallback: usar só o ID
              setConversaAtiva({ user_id: targetInfo.conversaUserId, user_nome: 'Utilizador', user_role: '' });
            }
          };
          
          buscarDadosUtilizador();
        }
      } else {
        setModoChat('canais');
        
        // Se tem canal específico, abrir esse canal
        if (targetInfo.canal) {
          setCanalAtivo(targetInfo.canal);
        }
      }
      
      // Fazer scroll para a PRIMEIRA MENSAGEM NÃO LIDA após carregar mensagens
      setTimeout(() => {
        scrollToFirstUnread();
      }, 800);
      
      // Limpar o targetInfo após processar
      onTargetHandled();
    }
  }, [targetInfo, onTargetHandled]);

  // Efeito para recarregar mensagens quando o canal muda
  useEffect(() => {
    if (modoChat === 'canais') {
      setLoading(true);
      carregarMensagens();
    }
  }, [canalAtivo]);

  // Efeito para scroll automático quando scrollToUnread é true
  useEffect(() => {
    if (scrollToUnread && !loading) {
      scrollToFirstUnread();
    }
  }, [scrollToUnread, loading, scrollToFirstUnread]);

  const carregarMensagens = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/chat/mensagens?canal=${canalAtivo}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMensagens(response.data || []);
      
      // NÃO marcar automaticamente como lida ao carregar
      // O utilizador deve ver a badge "Nova" primeiro
      // A mensagem só é marcada como lida quando o utilizador clica nela ou faz scroll
      
      // Atualizar contagem de não lidas
      carregarNaoLidas();
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    } finally {
      setLoading(false);
    }
  }, [canalAtivo, user?.id]);

  // Função para marcar uma mensagem específica como lida
  const marcarMensagemComoLida = async (msgId) => {
    try {
      const token = localStorage.getItem('token');
      await api.post(`/chat/mensagens/${msgId}/ler`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Atualizar estado local para refletir a leitura
      setMensagens(prev => prev.map(msg => {
        if (msg.id === msgId) {
          const userId = String(user?.id || '');
          const userUuid = String(user?.uuid || '');
          const jaLeu = msg.lido_por?.some(l => {
            const leituraUserId = String(l.user_id);
            return leituraUserId === userId || leituraUserId === userUuid;
          });
          
          if (!jaLeu) {
            return {
              ...msg,
              lido_por: [...(msg.lido_por || []), { user_id: userId, user_nome: user?.nome }]
            };
          }
        }
        return msg;
      }));
      
      carregarNaoLidas();
    } catch (error) {
      console.error('Erro ao marcar mensagem como lida:', error);
    }
  };

  const carregarNaoLidas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/chat/nao-lidas/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNaoLidas(response.data?.count || 0);
    } catch (error) {
      console.error('Erro ao contar não lidas:', error);
    }
  };

  // ==================== FUNÇÕES PARA MENSAGENS DIRETAS ====================
  
  const carregarDestinatarios = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/chat/direto/destinatarios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDestinatarios(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar destinatários:', error);
    }
  };

  const carregarConversas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/chat/direto/conversas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversas(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
    }
  };

  const carregarMensagensDiretas = async (outroUserId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/chat/direto/mensagens/${outroUserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMensagensDiretas(response.data || []);
      setTimeout(scrollToBottom, 100);
      
      // Marcar mensagens como lidas
      try {
        await api.post(`/chat/direto/marcar-lidas/${outroUserId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Recarregar para atualizar UI
        const updatedResponse = await api.get(`/chat/direto/mensagens/${outroUserId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMensagensDiretas(updatedResponse.data || []);
        carregarNaoLidasDiretas();
      } catch (e) {
        console.error('Erro ao marcar como lidas:', e);
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens diretas:', error);
    }
  };

  const carregarNaoLidasDiretas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/chat/direto/nao-lidas/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNaoLidasDiretas(response.data?.count || 0);
    } catch (error) {
      console.error('Erro ao contar não lidas diretas:', error);
    }
  };

  const enviarMensagemDireta = async () => {
    if (!novaMensagem.trim() || !conversaAtiva) return;
    
    setEnviando(true);
    try {
      const token = localStorage.getItem('token');
      await api.post(`/chat/direto/mensagens`, {
        destinatario_id: conversaAtiva.user_id,
        conteudo: novaMensagem.trim(),
        anexos: anexos
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNovaMensagem('');
      setAnexos([]);
      carregarMensagensDiretas(conversaAtiva.user_id);
      carregarConversas();
      toast.success('Mensagem enviada!');
    } catch (error) {
      console.error('Erro ao enviar mensagem direta:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setEnviando(false);
    }
  };

  const iniciarConversa = (destinatario) => {
    setConversaAtiva({
      user_id: destinatario.id,
      user_nome: destinatario.nome,
      user_role: destinatario.role
    });
    carregarMensagensDiretas(destinatario.id);
  };

  const abrirConversa = (conversa) => {
    setConversaAtiva(conversa);
    carregarMensagensDiretas(conversa.user_id);
    // Scroll para mensagens não lidas após carregar
    setTimeout(scrollToFirstUnread, 500);
  };

  useEffect(() => {
    if (modoChat === 'direto') {
      carregarDestinatarios();
      carregarConversas();
      carregarNaoLidasDiretas();
    }
  }, [modoChat]);

  useEffect(() => {
    if (conversaAtiva) {
      console.log('ChatInterno: conversaAtiva mudou:', conversaAtiva);
      const userId = conversaAtiva.user_id || conversaAtiva;
      console.log('ChatInterno: A carregar mensagens para user_id:', userId);
      // Carregar mensagens imediatamente ao seleccionar conversa
      carregarMensagensDiretas(userId);
      
      // Polling de mensagens diretas a cada 15 segundos
      const interval = setInterval(() => carregarMensagensDiretas(userId), 15000);
      return () => clearInterval(interval);
    }
  }, [conversaAtiva]);

  useEffect(() => {
    carregarMensagens();
    carregarNaoLidasDiretas();
    // Atualizar a cada 45 segundos (aumentado de 30s)
    const interval = setInterval(carregarMensagens, 45000);
    return () => clearInterval(interval);
  }, [carregarMensagens]);

  useEffect(() => {
    scrollToBottom();
  }, [mensagens]);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const uploadedFiles = [];

      for (const file of files) {
        // Validar tamanho (10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: Ficheiro muito grande (máx 10MB)`);
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post(`/chat/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        uploadedFiles.push(response.data.file);
      }

      setAnexos([...anexos, ...uploadedFiles]);
      toast.success(`${uploadedFiles.length} ficheiro(s) anexado(s)`);
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error('Erro ao fazer upload do ficheiro');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removerAnexo = (index) => {
    setAnexos(anexos.filter((_, i) => i !== index));
  };

  const enviarMensagem = async () => {
    if (!novaMensagem.trim() && anexos.length === 0) return;
    
    setEnviando(true);
    try {
      const token = localStorage.getItem('token');
      await api.post(`/chat/mensagens`, {
        canal: canalAtivo,
        conteudo: novaMensagem.trim(),
        tipo: 'normal',
        resposta_a: respondendoA?.id || null,
        anexos: anexos
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNovaMensagem('');
      setRespondendoA(null);
      setAnexos([]);
      carregarMensagens();
      toast.success('Mensagem enviada!');
      
      // Chamar callback para atualizar badge no dashboard
      if (onMensagemEnviada) {
        onMensagemEnviada();
      }
    } catch (error) {
      console.error('Erro ao enviar:', error);
      toast.error('Erro ao enviar mensagem');
    } finally {
      setEnviando(false);
    }
  };

  const fixarMensagem = async (mensagemId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`/chat/mensagens/${mensagemId}/fixar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.fixada ? 'Mensagem fixada!' : 'Mensagem desfixada!');
      carregarMensagens();
    } catch (error) {
      console.error('Erro ao fixar:', error);
      toast.error('Erro ao fixar mensagem');
    }
  };

  const eliminarMensagem = async (mensagemId) => {
    if (!window.confirm('Tem a certeza que quer eliminar esta mensagem?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/chat/mensagens/${mensagemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mensagem eliminada!');
      carregarMensagens();
    } catch (error) {
      console.error('Erro ao eliminar:', error);
      toast.error('Erro ao eliminar mensagem');
    }
  };

  const eliminarPermanentemente = async (mensagemId) => {
    if (!window.confirm('⚠️ ATENÇÃO: Esta ação é IRREVERSÍVEL!\n\nTem a certeza que quer eliminar permanentemente esta mensagem da base de dados?\n\nA mensagem será removida para sempre e não poderá ser recuperada.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/chat/mensagens/${mensagemId}/permanente`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mensagem eliminada permanentemente!');
      carregarMensagens();
    } catch (error) {
      console.error('Erro ao eliminar permanentemente:', error);
      toast.error(error.response?.data?.detail || 'Erro ao eliminar mensagem permanentemente');
    }
  };

  const carregarEstatisticas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/chat/estatisticas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEstatisticas(response.data);
      setShowEstatisticas(true);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      toast.error('Erro ao carregar estatísticas');
    }
  };

  const exportarRelatorio = async (canal = null) => {
    try {
      const token = localStorage.getItem('token');
      const url = canal 
        ? `/chat/relatorio?canal=${canal}&incluir_eliminadas=true`
        : `/chat/relatorio?incluir_eliminadas=true`;
      
      const response = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const relatorio = response.data;
      
      // Criar conteúdo do relatório em texto
      let conteudo = `RELATÓRIO DO CHAT INTERNO\n`;
      conteudo += `${'='.repeat(50)}\n\n`;
      conteudo += `Gerado em: ${new Date(relatorio.gerado_em).toLocaleString('pt-PT')}\n`;
      conteudo += `Canal: ${relatorio.filtros.canal}\n`;
      conteudo += `Total de mensagens: ${relatorio.resumo.total_mensagens}\n`;
      conteudo += `Mensagens eliminadas: ${relatorio.resumo.mensagens_eliminadas}\n`;
      conteudo += `Total de utilizadores: ${relatorio.resumo.total_usuarios}\n\n`;
      conteudo += `${'='.repeat(50)}\n`;
      conteudo += `MENSAGENS\n`;
      conteudo += `${'='.repeat(50)}\n\n`;
      
      for (const msg of relatorio.mensagens) {
        conteudo += `${'-'.repeat(40)}\n`;
        conteudo += `De: ${msg.autor}\n`;
        conteudo += `Data: ${new Date(msg.data_hora).toLocaleString('pt-PT')}\n`;
        conteudo += `Canal: ${msg.canal}\n`;
        if (msg.fixada) conteudo += `[FIXADA]\n`;
        if (msg.eliminada) {
          conteudo += `[ELIMINADA por ${msg.eliminada_por} em ${new Date(msg.eliminada_em).toLocaleString('pt-PT')}]\n`;
        }
        conteudo += `\nMensagem:\n${msg.conteudo}\n\n`;
        conteudo += `Leituras: ${msg.leituras.total} (${msg.leituras.percentagem}%)\n`;
        conteudo += `Leram: ${msg.leituras.utilizadores.map(u => u.nome).join(', ') || 'Ninguém'}\n`;
        conteudo += `Não leram: ${msg.leituras.nao_leram.join(', ') || 'Todos leram'}\n\n`;
      }
      
      // Download do ficheiro
      const blob = new Blob([conteudo], { type: 'text/plain;charset=utf-8' });
      const url2 = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url2;
      a.download = `relatorio_chat_${canal || 'todos'}_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url2);
      
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
      toast.error('Erro ao exportar relatório');
    }
  };

  const formatarData = (dataStr) => {
    if (!dataStr) return '';
    const data = new Date(dataStr);
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(ontem.getDate() - 1);
    
    if (data.toDateString() === hoje.toDateString()) {
      return `Hoje ${data.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (data.toDateString() === ontem.toDateString()) {
      return `Ontem ${data.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return data.toLocaleDateString('pt-PT', { 
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
    });
  };

  const canalInfo = CANAIS.find(c => c.id === canalAtivo) || CANAIS[0];
  const mensagensFixadas = mensagens.filter(m => m.fixada && !m.eliminada);
  const mensagensEliminadas = mensagens.filter(m => m.eliminada);
  const mensagensNormais = mensagens.filter(m => !m.fixada && !m.eliminada);
  const isAdmin = user?.role === 'admin';

  // Obter contagem de colaboradores
  const [totalColaboradores, setTotalColaboradores] = useState(0);
  
  useEffect(() => {
    const fetchColaboradores = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get(`/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTotalColaboradores(response.data?.length || 0);
      } catch (error) {
        setTotalColaboradores(10); // Valor padrão
      }
    };
    fetchColaboradores();
  }, []);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            Chat Interno
            {(naoLidas + naoLidasDiretas) > 0 && (
              <Badge className="bg-red-500 text-white">{naoLidas + naoLidasDiretas} novas</Badge>
            )}
          </h2>
          <p className="text-sm text-muted-foreground">Comunicação interna da empresa</p>
        </div>
        
        {/* Botões Admin */}
        {isAdmin && modoChat === 'canais' && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={carregarEstatisticas}>
              <BarChart3 className="h-4 w-4 mr-1" />
              Estatísticas
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportarRelatorio(canalAtivo)}>
              <Download className="h-4 w-4 mr-1" />
              Exportar Canal
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportarRelatorio()}>
              <FileText className="h-4 w-4 mr-1" />
              Exportar Tudo
            </Button>
          </div>
        )}
      </div>

      {/* Tabs: Canais vs Mensagens Diretas */}
      <div className="flex gap-2 mb-4 border-b pb-2">
        <Button
          variant={modoChat === 'canais' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => { setModoChat('canais'); setConversaAtiva(null); }}
        >
          📢 Canais
          {naoLidas > 0 && <Badge className="ml-1 bg-red-500 text-white text-xs">{naoLidas}</Badge>}
        </Button>
        <Button
          variant={modoChat === 'direto' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setModoChat('direto')}
        >
          ✉️ Mensagens Diretas
          {naoLidasDiretas > 0 && <Badge className="ml-1 bg-red-500 text-white text-xs">{naoLidasDiretas}</Badge>}
        </Button>
      </div>

      {/* MODO CANAIS */}
      {modoChat === 'canais' && (
        <>
          {/* Canais */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
            {CANAIS.map(canal => (
              <Button
                key={canal.id}
                variant={canalAtivo === canal.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCanalAtivo(canal.id)}
                className="whitespace-nowrap"
              >
                <span className="mr-1">{canal.icon}</span>
                {canal.nome}
              </Button>
            ))}
          </div>

          {/* Área de Mensagens */}
          <Card className="flex-1 flex flex-col min-h-0">
            <CardHeader className={`py-3 ${canalInfo.cor} text-white rounded-t-lg`}>
              <CardTitle className="text-base flex items-center gap-2">
                <span>{canalInfo.icon}</span>
                {canalInfo.nome}
              </CardTitle>
            </CardHeader>
            
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0" style={{ maxHeight: '400px' }}>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <p className="text-muted-foreground">A carregar...</p>
                </div>
              ) : (
                <>
                  {/* Mensagens Fixadas */}
                  {mensagensFixadas.length > 0 && (
                <div className="space-y-2 border-b pb-4 mb-4">
                  <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                    <Pin className="h-3 w-3" /> MENSAGENS FIXADAS
                  </p>
                  {mensagensFixadas.map(msg => (
                    <MensagemCard 
                      key={msg.id} 
                      mensagem={msg}
                      mensagens={mensagens}
                      user={user}
                      isAdmin={isAdmin}
                      totalColaboradores={totalColaboradores}
                      onResponder={() => { setRespondendoA(msg); inputRef.current?.focus(); }}
                      onFixar={() => fixarMensagem(msg.id)}
                      onEliminar={() => eliminarMensagem(msg.id)}
                      onVerLeituras={() => setShowLeituras(msg)}
                      onMarcarLida={marcarMensagemComoLida}
                      formatarData={formatarData}
                    />
                  ))}
                </div>
              )}

              {/* Mensagens Normais */}
              {mensagensNormais.length === 0 && mensagensFixadas.length === 0 && mensagensEliminadas.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                  <MessageCircle className="h-12 w-12 mb-2 opacity-30" />
                  <p>Ainda não há mensagens neste canal</p>
                  <p className="text-sm">Seja o primeiro a escrever!</p>
                </div>
              ) : (
                (() => {
                  let primeiraUnreadEncontrada = false;
                  const userId = String(user?.id || '');
                  const userUuid = String(user?.uuid || '');
                  
                  return mensagensNormais.map(msg => {
                    // Verificar se o utilizador actual já leu esta mensagem
                    const jaLeu = msg.lido_por?.some(l => {
                      const leituraUserId = String(l.user_id || '');
                      return leituraUserId === userId || leituraUserId === userUuid;
                    });
                    const autorId = String(msg.autor_id || '');
                    const isAutor = autorId === userId || autorId === userUuid;
                    const isUnread = !jaLeu && !isAutor;
                    const isFirstUnread = isUnread && !primeiraUnreadEncontrada;
                    if (isFirstUnread) primeiraUnreadEncontrada = true;
                    
                    return (
                      <div key={msg.id} ref={isFirstUnread ? firstUnreadRef : null}>
                        <MensagemCard 
                          mensagem={msg}
                          mensagens={mensagens}
                          user={user}
                          isAdmin={isAdmin}
                          totalColaboradores={totalColaboradores}
                          onResponder={() => { setRespondendoA(msg); inputRef.current?.focus(); }}
                          onFixar={() => fixarMensagem(msg.id)}
                          onEliminar={() => eliminarMensagem(msg.id)}
                          onVerLeituras={() => setShowLeituras(msg)}
                          onMarcarLida={marcarMensagemComoLida}
                          formatarData={formatarData}
                        />
                      </div>
                    );
                  });
                })()
              )}
              
              {/* Mensagens Eliminadas (só admin) */}
              {isAdmin && mensagensEliminadas.length > 0 && (
                <div className="space-y-2 border-t pt-4 mt-4">
                  <p className="text-xs font-semibold text-red-500 flex items-center gap-1">
                    <Trash2 className="h-3 w-3" /> MENSAGENS ELIMINADAS ({mensagensEliminadas.length})
                  </p>
                  {mensagensEliminadas.map(msg => (
                    <MensagemCard 
                      key={msg.id} 
                      mensagem={msg}
                      mensagens={mensagens}
                      user={user}
                      isAdmin={isAdmin}
                      totalColaboradores={totalColaboradores}
                      onResponder={() => {}}
                      onFixar={() => {}}
                      onEliminar={() => eliminarPermanentemente(msg.id)}
                      onVerLeituras={() => setShowLeituras(msg)}
                      onMarcarLida={marcarMensagemComoLida}
                      formatarData={formatarData}
                      isEliminada={true}
                    />
                  ))}
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </>
          )}
        </CardContent>

        {/* Input de Mensagem */}
        <div className="p-4 border-t">
          {respondendoA && (
            <div className="mb-2 p-2 bg-muted rounded-lg flex items-center justify-between">
              <div className="text-sm">
                <span className="text-muted-foreground">Responder a </span>
                <span className="font-medium">{respondendoA.autor_nome}</span>
                <span className="text-muted-foreground">: {respondendoA.conteudo.substring(0, 50)}...</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setRespondendoA(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          {/* Preview de Anexos */}
          {anexos.length > 0 && (
            <div className="mb-2 p-2 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Paperclip className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">{anexos.length} ficheiro(s) anexado(s)</span>
              </div>
              <div className="space-y-1">
                {anexos.map((anexo, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white p-2 rounded text-sm">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {anexo.type?.startsWith('image/') ? (
                        <ImageIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                      ) : (
                        <FileText className="h-4 w-4 text-gray-600 flex-shrink-0" />
                      )}
                      <span className="truncate">{anexo.filename}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2 flex-shrink-0"
                      onClick={() => removerAnexo(idx)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="self-end"
              title="Anexar ficheiro"
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            <Textarea
              ref={inputRef}
              value={novaMensagem}
              onChange={(e) => setNovaMensagem(e.target.value)}
              placeholder="Escreva a sua mensagem..."
              className="min-h-[60px] resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  enviarMensagem();
                }
              }}
            />
            <Button 
              onClick={enviarMensagem} 
              disabled={(!novaMensagem.trim() && anexos.length === 0) || enviando || uploading}
              className="self-end"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Pressione Enter para enviar, Shift+Enter para nova linha. Máx 10MB por ficheiro.
          </p>
        </div>
      </Card>
        </>
      )}

      {/* MODO MENSAGENS DIRETAS */}
      {modoChat === 'direto' && (
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 flex-1 min-h-0">
          {/* Lista de Conversas/Destinatários - esconde em mobile quando há conversa ativa */}
          <div className={`${conversaAtiva ? 'hidden md:flex' : 'flex'} w-full md:w-64 flex-col`}>
            <Card className="flex-1 flex flex-col">
              <CardHeader className="py-2 md:py-3 bg-purple-600 text-white rounded-t-lg">
                <CardTitle className="text-sm md:text-base">
                  {conversaAtiva ? '← Conversas' : 'Destinatários'}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto p-2 space-y-1 max-h-[300px] md:max-h-none">
                {!conversaAtiva ? (
                  <>
                    {/* Conversas Existentes */}
                    {conversas.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-muted-foreground px-2 mb-1">Conversas Recentes</p>
                        {conversas.map(conv => (
                          <div
                            key={conv.user_id}
                            className="p-2 rounded cursor-pointer hover:bg-muted flex justify-between items-center"
                            onClick={() => abrirConversa(conv)}
                          >
                            <div>
                              <p className="font-medium text-sm">{conv.user_nome}</p>
                              <p className="text-xs text-muted-foreground truncate max-w-[140px]">
                                {conv.ultima_mensagem}
                              </p>
                            </div>
                            {conv.nao_lidas > 0 && (
                              <Badge className="bg-red-500 text-white text-xs">{conv.nao_lidas}</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Novos Destinatários */}
                    <div>
                      <p className="text-xs text-muted-foreground px-2 mb-1">Iniciar Nova Conversa</p>
                      {destinatarios.map(dest => (
                        <div
                          key={dest.id}
                          className="p-2 rounded cursor-pointer hover:bg-muted flex justify-between items-center"
                          onClick={() => iniciarConversa(dest)}
                        >
                          <span className="text-sm">{dest.nome}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {dest.role_label}
                          </Badge>
                        </div>
                      ))}
                      {destinatarios.length === 0 && (
                        <p className="text-xs text-muted-foreground text-center py-4">
                          Nenhum destinatário disponível
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full justify-start"
                    onClick={() => setConversaAtiva(null)}
                  >
                    ← Voltar às conversas
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Área de Conversa - mostra botão voltar em mobile */}
          <Card className={`${!conversaAtiva ? 'hidden md:flex' : 'flex'} flex-1 flex-col min-h-0`}>
            {conversaAtiva ? (
              <>
                <CardHeader className="py-2 md:py-3 bg-purple-600 text-white rounded-t-lg">
                  <CardTitle className="text-sm md:text-base flex items-center gap-2">
                    <button 
                      onClick={() => setConversaAtiva(null)}
                      className="md:hidden p-1 hover:bg-purple-700 rounded"
                    >
                      ←
                    </button>
                    ✉️ {conversaAtiva.user_nome}
                    <Badge variant="outline" className="text-white border-white text-[10px]">
                      {conversaAtiva.user_role === 'admin' ? 'Admin' : conversaAtiva.user_role === 'responsavel' ? 'Responsável' : 'Colaborador'}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="flex-1 overflow-y-auto p-2 md:p-4 space-y-2 md:space-y-3" style={{ maxHeight: '350px' }}>
                  {mensagensDiretas.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-muted-foreground">Nenhuma mensagem ainda. Inicie a conversa!</p>
                    </div>
                  ) : (
                    (() => {
                      let primeiraUnreadEncontrada = false;
                      return mensagensDiretas.map(msg => {
                        const remetenteId = String(msg.remetente_id || '');
                        const userId = String(user?.id || '');
                        const userUuid = String(user?.uuid || '');
                        const isMinhaMsg = remetenteId === userId || remetenteId === userUuid;
                        const isNaoLida = !isMinhaMsg && !msg.lida;
                        const isFirstUnread = isNaoLida && !primeiraUnreadEncontrada;
                        if (isFirstUnread) primeiraUnreadEncontrada = true;
                        
                        return (
                          <div
                            key={msg.id}
                            ref={isFirstUnread ? firstUnreadRef : null}
                            className={`p-3 rounded-lg max-w-[80%] ${
                              isMinhaMsg
                                ? 'ml-auto bg-purple-100'
                                : isNaoLida
                                  ? 'mr-auto bg-blue-50 border-l-4 border-l-blue-500'
                                  : 'mr-auto bg-muted'
                            }`}
                          >
                            {/* Badge Nova para mensagens não lidas */}
                            {isNaoLida && (
                              <Badge className="bg-blue-500 text-white text-[10px] px-1 py-0 mb-1">Nova</Badge>
                            )}
                          
                          <p className="text-sm">{msg.conteudo}</p>
                          
                          {/* Mostrar anexos da mensagem direta */}
                          {msg.anexos && msg.anexos.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {msg.anexos.map((anexo, idx) => {
                                const isImage = anexo.type?.startsWith('image/') || anexo.filename?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                                return (
                                  <div key={idx}>
                                    {isImage ? (
                                      <a href={`${API_URL}${anexo.url}`} target="_blank" rel="noopener noreferrer">
                                        <img 
                                          src={`${API_URL}${anexo.url}`} 
                                          alt={anexo.filename}
                                          className="max-w-[200px] rounded cursor-pointer hover:opacity-90"
                                        />
                                      </a>
                                    ) : (
                                      <a 
                                        href={`${API_URL}${anexo.url}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                      >
                                        <FileText className="h-3 w-3" />
                                        {anexo.filename}
                                      </a>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          <div className={`flex items-center gap-2 mt-1 ${isMinhaMsg ? 'justify-end' : 'justify-between'}`}>
                            {!isMinhaMsg && (
                              <span className="text-[10px] text-muted-foreground">
                                {msg.lida ? (
                                  <span className="flex items-center gap-1">
                                    <CheckCheck className="h-3 w-3 text-blue-500" />
                                    Lida
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1 text-blue-600 font-medium">
                                    <Check className="h-3 w-3" />
                                    Não lida
                                  </span>
                                )}
                              </span>
                            )}
                            <span className="text-[10px] text-muted-foreground">
                              {new Date(msg.created_at).toLocaleString('pt-PT', {
                                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                              })}
                            </span>
                            {isMinhaMsg && (
                              <span title={msg.lida ? 'Lida pelo destinatário' : 'Enviada, aguardando leitura'}>
                                {msg.lida 
                                  ? <CheckCheck className="h-3 w-3 text-blue-500" />
                                  : <Check className="h-3 w-3 text-gray-400" />
                                }
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    });
                    })()
                  )}
                  <div ref={messagesEndRef} />
                </CardContent>

                {/* Input de Mensagem Direta */}
                <div className="p-3 border-t">
                  {/* Mostrar anexos pendentes */}
                  {anexos.length > 0 && (
                    <div className="mb-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Paperclip className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-900">{anexos.length} ficheiro(s) anexado(s)</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {anexos.map((anexo, idx) => (
                          <div key={idx} className="flex items-center gap-1 bg-white px-2 py-1 rounded text-xs border">
                            {anexo.type?.startsWith('image/') ? (
                              <ImageIcon className="h-3 w-3 text-green-600 flex-shrink-0" />
                            ) : (
                              <File className="h-3 w-3 text-blue-600 flex-shrink-0" />
                            )}
                            <span className="truncate max-w-[100px]">{anexo.filename}</span>
                            <button 
                              onClick={() => removerAnexo(idx)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="self-end"
                      title="Anexar ficheiro"
                    >
                      <Paperclip className="h-4 w-4" />
                    </Button>
                    <Textarea
                      ref={inputRef}
                      value={novaMensagem}
                      onChange={(e) => setNovaMensagem(e.target.value)}
                      placeholder="Escreva a sua mensagem..."
                      className="min-h-[40px] max-h-[100px] resize-none"
                      rows={1}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          enviarMensagemDireta();
                        }
                      }}
                    />
                    <Button 
                      onClick={enviarMensagemDireta} 
                      disabled={(!novaMensagem.trim() && anexos.length === 0) || enviando || uploading}
                      className="self-end"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Máx 10MB por ficheiro
                  </p>
                </div>
              </>
            ) : (
              <CardContent className="flex items-center justify-center h-full">
                <div className="text-center">
                  <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">Selecione uma conversa ou inicie uma nova</p>
                  {user?.role === 'colaborador' && (
                    <p className="text-xs text-muted-foreground mt-2">
                      💡 Como colaborador, pode enviar mensagens apenas para Admin ou Responsável
                    </p>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      )}

      {/* Modal de Leituras */}
      <Dialog open={!!showLeituras} onOpenChange={() => setShowLeituras(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Quem leu esta mensagem
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {showLeituras?.lido_por?.length > 0 ? (
              showLeituras.lido_por.map((leitura, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="font-medium">{leitura.user_nome}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(leitura.data).toLocaleDateString('pt-PT', {
                      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                    })}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-center py-4">Ninguém leu ainda</p>
            )}
          </div>
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              <CheckCheck className="h-4 w-4 inline mr-1 text-blue-500" />
              {showLeituras?.lido_por?.length || 0} de {totalColaboradores} colaboradores leram
            </p>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal de Estatísticas */}
      <Dialog open={showEstatisticas} onOpenChange={setShowEstatisticas}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Estatísticas do Chat
            </DialogTitle>
          </DialogHeader>
          {estatisticas && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-700">{estatisticas.total}</p>
                  <p className="text-xs text-blue-600">Total de mensagens</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-700">{estatisticas.eliminadas || 0}</p>
                  <p className="text-xs text-red-600">Eliminadas</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-700">{estatisticas.hoje}</p>
                  <p className="text-xs text-green-600">Hoje</p>
                </div>
              </div>
              
              <div>
                <p className="font-medium mb-2">Por Canal:</p>
                <div className="space-y-2">
                  {CANAIS.map(canal => (
                    <div key={canal.id} className="flex justify-between items-center p-2 bg-muted rounded">
                      <span>{canal.icon} {canal.nome}</span>
                      <Badge variant="outline">{estatisticas.por_canal?.[canal.id] || 0}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Componente de Mensagem Individual
const MensagemCard = ({ 
  mensagem, mensagens = [], user, isAdmin, totalColaboradores,
  onResponder, onFixar, onEliminar, onVerLeituras, onMarcarLida, formatarData,
  isEliminada = false
}) => {
  // Verificar autoria - comparar com id OU uuid do user
  const userId = String(user?.id || '');
  const userUuid = String(user?.uuid || '');
  const autorId = String(mensagem.autor_id || '');
  const isAutor = autorId === userId || autorId === userUuid;
  
  const leituras = mensagem.lido_por?.length || 0;
  
  // Verificar se o utilizador atual já leu esta mensagem
  const jaLeu = mensagem.lido_por?.some(l => {
    const leituraUserId = String(l.user_id);
    return leituraUserId === userId || leituraUserId === userUuid;
  });
  const isNova = !isAutor && !jaLeu;
  
  // Marcar como lida quando clicar na mensagem (apenas se for nova)
  const handleClick = () => {
    if (isNova && onMarcarLida) {
      onMarcarLida(mensagem.id);
    }
  };
  
  return (
    <div 
      id={`msg-${mensagem.id}`}
      onClick={handleClick}
      className={`p-3 rounded-lg cursor-pointer transition-all duration-300 ${
      isEliminada 
        ? 'bg-red-50/50 border border-red-200 opacity-70' 
        : mensagem.fixada 
          ? 'bg-yellow-50 border border-yellow-200' 
          : isNova
            ? 'bg-blue-100 border-l-4 border-l-red-500 border border-blue-300 shadow-md'
            : 'bg-muted/50'
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">{mensagem.autor_nome}</span>
          {mensagem.autor_role === 'admin' && (
            <Badge variant="outline" className="text-[10px] px-1 py-0">Admin</Badge>
          )}
          {mensagem.fixada && <Pin className="h-3 w-3 text-yellow-600" />}
          {isNova && (
            <Badge className="bg-red-500 text-white text-[10px] px-2 py-0 animate-pulse font-bold">🔴 NOVA</Badge>
          )}
          {isEliminada && (
            <Badge variant="destructive" className="text-[10px] px-1 py-0">
              Eliminada por {mensagem.eliminada_por}
            </Badge>
          )}
        </div>
        <span className="text-xs text-muted-foreground">{formatarData(mensagem.created_at)}</span>
      </div>
      
      {/* Info de eliminação */}
      {isEliminada && mensagem.eliminada_em && (
        <p className="text-xs text-red-500 mb-1">
          Eliminada em {new Date(mensagem.eliminada_em).toLocaleString('pt-PT')}
        </p>
      )}
      
      {/* Resposta a */}
      {mensagem.resposta_a && (
        <div 
          className="mb-2 pl-2 border-l-2 border-primary/50 text-xs text-muted-foreground bg-muted/30 rounded-r py-1 pr-2 cursor-pointer hover:bg-muted/50"
          onClick={() => {
            // Scroll para a mensagem original
            const el = document.getElementById(`msg-${mensagem.resposta_a}`);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.classList.add('ring-2', 'ring-primary');
              setTimeout(() => el.classList.remove('ring-2', 'ring-primary'), 2000);
            }
          }}
        >
          {(() => {
            // Encontrar a mensagem original
            const msgOriginal = mensagens.find(m => m.id === mensagem.resposta_a);
            if (msgOriginal) {
              const textoPreview = msgOriginal.conteudo?.length > 80 
                ? msgOriginal.conteudo.substring(0, 80) + '...' 
                : msgOriginal.conteudo || '[Anexo]';
              return (
                <>
                  <span className="font-medium text-primary">{msgOriginal.autor_nome}</span>
                  <p className="truncate">{textoPreview}</p>
                </>
              );
            }
            return <span>Em resposta a uma mensagem</span>;
          })()}
        </div>
      )}
      
      {/* Conteúdo */}
      {mensagem.conteudo && (
        <p className={`text-sm whitespace-pre-wrap mb-2 ${isEliminada ? 'italic' : ''}`}>{mensagem.conteudo}</p>
      )}
      
      {/* Anexos */}
      {mensagem.anexos && mensagem.anexos.length > 0 && (
        <div className="mb-2 space-y-2">
          {mensagem.anexos.map((anexo, idx) => {
            const isImage = anexo.type?.startsWith('image/') || anexo.filename?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
            const isPdf = anexo.type === 'application/pdf' || anexo.filename?.endsWith('.pdf');
            
            return (
              <div key={idx} className="border rounded-lg overflow-hidden bg-white">
                {isImage ? (
                  <a href={`${API_URL}${anexo.url}`} target="_blank" rel="noopener noreferrer">
                    <img 
                      src={`${API_URL}${anexo.url}`} 
                      alt={anexo.filename}
                      className="max-w-full h-auto max-h-64 object-contain cursor-pointer hover:opacity-90"
                    />
                  </a>
                ) : (
                  <a 
                    href={`${API_URL}${anexo.url}`}
                    download={anexo.filename}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50"
                  >
                    {isPdf ? <FileText className="h-5 w-5 text-red-500" /> : <File className="h-5 w-5 text-gray-500" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{anexo.filename}</p>
                      <p className="text-xs text-gray-500">{(anexo.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <Download className="h-4 w-4 text-gray-400" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Footer */}
      <div className="flex items-center justify-between">
        <div 
          className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer hover:text-foreground"
          onClick={onVerLeituras}
          title="Ver quem leu"
        >
          {isAutor ? (
            // Mensagem enviada por mim - mostrar estado de leitura
            leituras > 1 ? (
              <>
                <CheckCheck className="h-3 w-3 text-blue-500" />
                <span>{leituras - 1} leram</span>
              </>
            ) : (
              <>
                <Check className="h-3 w-3 text-gray-400" />
                <span>Enviada</span>
              </>
            )
          ) : (
            // Mensagem de outro - mostrar se eu li
            jaLeu ? (
              <>
                <CheckCheck className="h-3 w-3 text-blue-500" />
                <span>{leituras}/{totalColaboradores} leram</span>
              </>
            ) : (
              <>
                <span className="text-red-500 font-bold animate-pulse">⬤ CLIQUE PARA MARCAR COMO LIDA</span>
              </>
            )
          )}
        </div>
        
        {/* Botões de ação */}
        {!isEliminada ? (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-6 px-2" onClick={onResponder}>
              <Reply className="h-3 w-3" />
            </Button>
            {isAdmin && (
              <Button variant="ghost" size="sm" className="h-6 px-2" onClick={onFixar}>
                <Pin className={`h-3 w-3 ${mensagem.fixada ? 'text-yellow-600' : ''}`} />
              </Button>
            )}
            {(isAutor || isAdmin) && (
              <Button variant="ghost" size="sm" className="h-6 px-2 text-red-500 hover:text-red-700" onClick={onEliminar}>
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        ) : isAdmin && (
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 px-2 text-red-600 hover:text-red-800 hover:bg-red-100" 
              onClick={onEliminar}
              title="Eliminar permanentemente da base de dados"
            >
              <Trash2 className="h-3 w-3" />
              <X className="h-3 w-3 -ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatInterno;
